/* =============================================================================
   AQUORIX — ManifestPage.tsx
   Path: aquorix-frontend % src/features/dashboard/pages/ManifestPage.tsx
   Version 1.5 · 29 July 2026 · Owner: Larry McLean · Author: Claude
   Faithful React port of AQUORIX — Manifest · Interactive Prototype v3 (approved).
   Transcription, not a redesign: same DOM, same class names, same values, same copy.
   Behaviour is React state/handlers; the emitted markup matches the prototype.

   The data seam is useManifestData.ts. This file is the locked visual and is not
   edited during wiring. DEMO_MODE (in the seam) ships it on the fixture.

   Change log:
   1.0  2026-07-27  Initial port of prototype v3.
   1.1  2026-07-28  Build fix: removed the ": JSX.Element" return annotation (rejected
                    by the app's tsc). Portaled the modal + toast to <body> via
                    createPortal so position:fixed escapes the dashboard shell.
   1.2  2026-07-28  No TSX change (the z-index correction was CSS-only).
   1.3  2026-07-28  Renamed the preview box className "modal" -> "manifest-modal" to
                    match the CSS: a global .modal component in the app shell was
                    hiding the box. One className only; all other classes unchanged.
   1.4  2026-07-28  Live-data wiring (with useManifestData v1.1): the roster now
                    re-syncs from the async hook, and an on-brand empty/loading state
                    renders when a day has no trips so the page never crashes on real
                    (possibly empty) data. Data flow only — no change to the roster,
                    readiness, crew, action-bar, modal, or print markup.
   1.5  2026-07-29  Persist identity edits via saveIdentity; wire generate + gender column across MVP repair.
   ============================================================================= */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ManifestPage.css";
import {
  useManifestData, saveIdentity, generateManifestFile, ACTIVITIES, NATIONALITIES,
  type ManifestData, type Trip, type Person, type Scope,
} from "./useManifestData";

/* ---- jurisdictions: one surface, authority-aware output ---- */
type JurisCode = "cdws" | "aseza";
interface Juris { code: JurisCode; label: string; short: string; built: boolean; center: string; genLabel: string; }
const JURIS: Record<JurisCode, Juris> = {
  cdws: { code: "cdws", label: "Egypt · CDWS Trip Notification", short: "CDWS", built: true, center: "Nesima Diving Center · Dahab", genLabel: "Generate CDWS file" },
  aseza: { code: "aseza", label: "Aqaba · ASEZA Submission", short: "ASEZA", built: false, center: "Aqaba dive operator · Red Sea", genLabel: "Prepare ASEZA roster" },
};

const REQUIRED: (keyof Person)[] = ["nationality", "passport", "dob", "gender"];
const HEADERS = ["FirstName", "LastName", "Birthdate", "Nationality", "Gender", "PassportNumber", "Activity", "Accomodation", "RoomNumber", "ResponsiblePassportNumber", "ResponsibleNationality"];

/* ---- pure helpers ---- */
function missingFields(pn: Person): string[] {
  const m: string[] = [];
  REQUIRED.forEach((f) => { if (!pn[f]) m.push(f as string); });
  if (pn.isMinor) { if (!pn.guardianPassport) m.push("guardianPassport"); if (!pn.guardianNationality) m.push("guardianNationality"); }
  return m;
}
function initials(first: string, last: string): string { return ((first[0] || "") + (last[0] || "")).toUpperCase() || "?"; }
function actClass(a: string): string { if (a === "Snorkeling" || a === "Free Diving") return "snork"; if (a === "Passenger") return "pass"; return ""; }
function maskPass(v: string): string { const s = String(v); return s.length > 4 ? s[0] + "•••••" + s.slice(-3) : s; }
function rosterBreakdown(r: Person[]): string {
  const c: Record<string, number> = {};
  r.forEach((p) => { const k = p.activity === "Snorkeling" ? "snorkeler" : p.activity === "Passenger" ? "passenger" : p.activity === "Free Diving" ? "free diver" : "diver"; c[k] = (c[k] || 0) + 1; });
  return Object.entries(c).map(([k, n]) => n + " " + k + (n > 1 ? "s" : "")).join(" · ");
}

/* ---- icons (SVG line-art, no emoji) ---- */
const CheckIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>);
const WarnIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>);
const HarvestIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 6.9H21l-5.4 4 2.1 6.6L12 15.9 6.3 19.5l2.1-6.6L3 8.9h6.6z" /></svg>);
const Chevron = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>);
const ShieldIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>);
const BoatIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18h18M5 18l1-7h12l1 7M8 11V7a4 4 0 0 1 8 0v4" /></svg>);
const WaveIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12c2 0 2.5-2 4.5-2s2.5 2 4.5 2 2.5-2 4.5-2 2.5 2 4.5 2M2 17c2 0 2.5-2 4.5-2s2.5 2 4.5 2 2.5-2 4.5-2 2.5 2 4.5 2" /></svg>);
const ClockIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>);
const PlusIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>);
const PrintIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6Z" /></svg>);
const DownloadIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>);
const InfoIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>);

interface Editing { pid: number; field: keyof Person; }

export default function ManifestPage() {
  const initial: ManifestData = useManifestData();
  const [trips, setTrips] = useState<Trip[]>(initial.trips);
  const [scope, setScope] = useState<Scope>("boat");
  const [tripIdx, setTripIdx] = useState(0);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [juris, setJuris] = useState<JurisCode>("cdws");
  const [modal, setModal] = useState<JurisCode | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedGuides = useRef<Record<string, string>>({});

  // useManifestData delivers live data asynchronously (empty first, then the fetched
  // day). Load it into the editable roster state whenever it changes — on mount and
  // again when the fetch resolves. In DEMO_MODE the fixture arrives once and stays.
  useEffect(() => { setTrips(initial.trips); }, [initial]);

  const currentTrips = useMemo(() => trips.filter((t) => t.scope === scope), [trips, scope]);
  const idx = Math.min(tripIdx, Math.max(0, currentTrips.length - 1));
  const trip = currentTrips[idx];

  useEffect(() => {
    if (!editing) return;
    const el = document.querySelector<HTMLInputElement | HTMLSelectElement>("[data-edit]");
    if (el) { el.focus(); if (el instanceof HTMLInputElement) el.select(); }
  }, [editing]);

  function toast(msg: string) {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 2600);
  }

  const readiness = useMemo(() => {
    const r = trip ? trip.roster : [];
    let complete = 0, blocking = 0;
    r.forEach((pn) => { if (missingFields(pn).length === 0) complete++; else blocking++; });
    return { total: r.length, complete, blocking };
  }, [trip]);

  /* ---- mutations (immutable) ---- */
  function updatePerson(pid: number, patch: Partial<Person>) {
    setTrips((ts) => ts.map((t) => ({ ...t, roster: t.roster.map((p) => (p.id === pid ? { ...p, ...patch } : p)) })));
  }
  const PERSISTED = ["dob", "gender", "passport", "guardianPassport", "nationality", "guardianNationality"];
  async function commitEdit(pid: number, field: keyof Person, value: string) {
    const v = value === "" ? null : value;
    updatePerson(pid, { [field]: v } as Partial<Person>);
    setEditing(null);
    const person = trips.flatMap((t) => t.roster).find((p) => p.id === pid) as (Person & { _added?: boolean }) | undefined;
    if (person && person._added) return;
    if (!PERSISTED.includes(field as string)) return;
    try {
      await saveIdentity(pid, field as string, v);
    } catch (_e) {
      toast("Could not save that field - check the connection and try again.");
    }
  }
  function startEdit(pid: number, field: keyof Person) { setEditing({ pid, field }); }
  function cancelEdit() { setEditing(null); }

  function addPassenger() {
    if (!trip) return;
    const nid = Math.max(0, ...trips.flatMap((t) => t.roster.map((p) => p.id))) + 1;
    const np: Person = { id: nid, first: "New", last: "Guest", nationality: null, passport: null, dob: null, gender: null, activity: "Passenger", isMinor: false, guardianPassport: null, guardianNationality: null, accommodation: null, cert: null, _added: true } as Person & { _added: boolean };
    setTrips((ts) => ts.map((t) => (t === trip ? { ...t, roster: [...t.roster, np] } : t)));
    toast("Added a walk-up passenger — everyone aboard needs identity for the notification.");
  }
  function removePerson(pid: number) {
    setTrips((ts) => ts.map((t) => ({ ...t, roster: t.roster.filter((p) => p.id !== pid) })));
  }
  function toggleGuide() {
    if (!trip) return;
    const key = trip.boat;
    if (trip.guide) { savedGuides.current[key] = trip.guide; setTrips((ts) => ts.map((t) => (t === trip ? { ...t, guide: null } : t))); toast("Guide set to TBD — readiness now flags it."); }
    else { const restored = savedGuides.current[key] || "Rami Mounir"; setTrips((ts) => ts.map((t) => (t === trip ? { ...t, guide: restored } : t))); toast("Guide assigned from the session."); }
  }
  function changeScope(s: Scope) { setScope(s); setTripIdx(0); setEditing(null); }
  function navTrip(d: number) { const n = currentTrips.length; if (n === 0) return; setTripIdx((idx + d + n) % n); setEditing(null); }
  function changeJuris(j: JurisCode) { setJuris(j); setEditing(null); }
  function openGenerate() { setModal(juris); }
  function printCopy() { window.print(); }
  async function downloadCdwsFile() {
    if (!trip || trip.session_id == null) { toast("No session to generate for."); return; }
    try {
      const blob = await generateManifestFile(trip.session_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "GuestList.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setModal(null);
    } catch (_e) {
      toast("Could not generate the file - please try again.");
    }
  }

  // No trip for this scope/day, or the live fetch has not resolved yet. This guard was
  // always here (blank); it now renders a safe on-brand empty state. Uses JURIS[juris]
  // directly because J is derived further down, after this point.
  if (!trip) {
    return (
      <div className="manifest-page">
        <div className="wrap">
          <div className="crown">
            <div className="crown-top">
              <div>
                <h1>Manifest</h1>
                <div className="juris"><ShieldIcon /> {JURIS[juris].label}</div>
              </div>
              <div className="scope">
                <button className={scope === "boat" ? "on" : ""} onClick={() => changeScope("boat")}><BoatIcon /> Trip · Boat</button>
                <button className={scope === "shore" ? "on" : ""} onClick={() => changeScope("shore")}><WaveIcon /> Day · Shore</button>
              </div>
            </div>
          </div>
          <div className="rcard" style={{ padding: "44px 24px", textAlign: "center", color: "var(--muted)", fontSize: 14, fontWeight: 600, marginTop: 14 }}>
            No {scope === "boat" ? "boat departures" : "shore days"} with booked divers to show for this day.
            {trips.length > 0 ? " Try the " + (scope === "boat" ? "Day · Shore" : "Trip · Boat") + " view." : ""}
          </div>
        </div>
      </div>
    );
  }

  const J = JURIS[juris];
  const go = readiness.blocking === 0;
  const guideOk = !!trip.guide;

  /* ---- inline field editor ---- */
  function fieldEditor(pn: Person, field: keyof Person) {
    const v = (pn[field] as string) || "";
    if (field === "nationality" || field === "guardianNationality") {
      return (<span className="editwrap"><select data-edit value={v} onChange={(e) => commitEdit(pn.id, field, e.target.value)} onBlur={cancelEdit}><option value="">— select —</option>{NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}</select></span>);
    }
    if (field === "activity") {
      return (<span className="editwrap"><select data-edit value={v} onChange={(e) => commitEdit(pn.id, field, e.target.value)} onBlur={cancelEdit}>{ACTIVITIES.map((a) => <option key={a} value={a}>{a}</option>)}</select></span>);
    }
    if (field === "gender") {
      return (<span className="editwrap"><select data-edit value={v} onChange={(e) => commitEdit(pn.id, field, e.target.value)} onBlur={cancelEdit}><option value="">— select —</option><option value="Male">Male</option><option value="Female">Female</option></select></span>);
    }
    if (field === "dob") {
      return (<span className="editwrap"><input data-edit type="date" defaultValue={v} max="2023-05-20" onChange={(e) => commitEdit(pn.id, field, e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") cancelEdit(); }} /></span>);
    }
    return (<span className="editwrap"><input data-edit type="text" defaultValue={v} placeholder="Passport number" onBlur={(e) => commitEdit(pn.id, field, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") cancelEdit(); }} /></span>);
  }
  function cell(pn: Person, field: keyof Person, label: string) {
    if (editing && editing.pid === pn.id && editing.field === field) return fieldEditor(pn, field);
    const v = pn[field] as string | null;
    if (v) return (<button className="cellbtn p-cell" onClick={() => startEdit(pn.id, field)}>{field === "passport" ? maskPass(v) : v}</button>);
    return (<button className="flag" onClick={() => startEdit(pn.id, field)}><WarnIcon /> Add {label.toLowerCase()}</button>);
  }
  function activityCell(pn: Person) {
    if (editing && editing.pid === pn.id && editing.field === "activity") return fieldEditor(pn, "activity");
    return (<button className={"activity " + actClass(pn.activity)} onClick={() => startEdit(pn.id, "activity")}>{pn.activity} <Chevron /></button>);
  }
  function statusCell(pn: Person) {
    const m = missingFields(pn);
    const rm = (pn as Person & { _added?: boolean })._added ? <button className="rmbtn" title="remove" onClick={() => removePerson(pn.id)}>×</button> : null;
    if (m.length === 0) return (<span className="p-status"><span className="st-ok"><CheckIcon /></span>{rm}</span>);
    return (<span className="p-status"><span className="st-count">{m.length} to fix</span>{rm}</span>);
  }
  function guardianRow(pn: Person) {
    if (!pn.isMinor) return null;
    const gp = editing && editing.pid === pn.id && editing.field === "guardianPassport"
      ? fieldEditor(pn, "guardianPassport")
      : (pn.guardianPassport ? <button className="cellbtn" onClick={() => startEdit(pn.id, "guardianPassport")}>Responsible passport {maskPass(pn.guardianPassport)}</button> : <button className="flag" onClick={() => startEdit(pn.id, "guardianPassport")}><WarnIcon /> Add responsible passport</button>);
    const gn = editing && editing.pid === pn.id && editing.field === "guardianNationality"
      ? fieldEditor(pn, "guardianNationality")
      : (pn.guardianNationality ? <button className="cellbtn" onClick={() => startEdit(pn.id, "guardianNationality")}>{pn.guardianNationality}</button> : <button className="flag" onClick={() => startEdit(pn.id, "guardianNationality")}><WarnIcon /> Add responsible nationality</button>);
    return (
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12, alignItems: "center", paddingTop: 8, marginTop: 2, borderTop: "1px dashed var(--line)", fontSize: 11.5, color: "var(--muted)", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", fontSize: 9.5, color: "var(--muted)" }}>Minor · responsible adult</span> {gp} {gn}
      </div>
    );
  }

  /* ---- action-bar copy ---- */
  const actionTitle = !J.built ? "Prepare the " + J.short + " roster" : go ? "Ready to generate the CDWS roster" : "Generate the CDWS roster";
  const actionSub = !J.built
    ? "The roster and readiness work the same way for Aqaba. The " + J.short + " output writer arrives when the official form is in hand — it never blocks the operator from assembling the roster."
    : go ? "The file will contain every practitioner with all required identity, in the exact CDWS template."
      : readiness.blocking + " practitioner" + (readiness.blocking > 1 ? "s" : "") + " still need required identity. You may generate now, but the file will be incomplete until they are fixed.";

  return (
    <div className="manifest-page">
      <div className="wrap">
        {/* CROWN */}
        <div className="crown">
          <div className="crown-top">
            <div>
              <h1>Manifest</h1>
              <div className="juris"><ShieldIcon /> {J.label}</div>
              <div className="crown-sub">{J.center} · assemble everyone on the {scope === "boat" ? "departure" : "day"}, clear what is missing, and produce the official roster.</div>
              <div className="juris-switch">
                <span className="jl">Preview jurisdiction</span>
                <button className={juris === "cdws" ? "on" : ""} onClick={() => changeJuris("cdws")}>Egypt · CDWS</button>
                <button className={juris === "aseza" ? "on" : ""} onClick={() => changeJuris("aseza")}>Aqaba · ASEZA</button>
              </div>
            </div>
            <div className="scope">
              <button className={scope === "boat" ? "on" : ""} onClick={() => changeScope("boat")}><BoatIcon /> Trip · Boat</button>
              <button className={scope === "shore" ? "on" : ""} onClick={() => changeScope("shore")}><WaveIcon /> Day · Shore</button>
            </div>
          </div>
          <div className="trip-row">
            <span className="trip-nav"><button onClick={() => navTrip(-1)}>‹</button></span>
            <span className="trip-pill">{scope === "boat" ? <BoatIcon /> : <WaveIcon />}<span className="tp-boat">{trip.boat}</span><span className="tp-time">· {trip.time}</span></span>
            <span className="trip-nav"><button onClick={() => navTrip(1)}>›</button></span>
            {scope === "shore" && <span className="shore-note"><ClockIcon /> Shore day — guests updatable until 17:00</span>}
          </div>
        </div>

        {/* READINESS */}
        <div className="ready">
          <div className={"verdict " + (go ? "go" : "")}>
            <div className="v-icon">{go ? <HarvestIcon /> : <WarnIcon />}</div>
            <div className="v-label">Can I file this?</div>
            <div className="v-big">{go ? "Ready to file" : readiness.blocking + " blocking"}</div>
            <div className="v-sub">{go ? "Every practitioner has the identity the authority requires. Generate whenever you are ready." : "These practitioners are missing a required field. Tap any flag to fill it in place."}</div>
          </div>
          <div className="rcard"><div className="rc-label">Practitioners</div><div className="rc-value">{readiness.total}</div><div className="rc-sub">{rosterBreakdown(trip.roster)}</div></div>
          <div className="rcard"><div className="rc-label">Identity complete</div><div className={"rc-value " + (go ? "go" : "warn")}>{readiness.complete} / {readiness.total}</div><div className="rc-sub">{go ? "All identity present" : readiness.blocking + " still missing a field"}</div></div>
          <div className="rcard"><div className="rc-label">Responsible guide</div><div className="rc-value" style={{ fontSize: 19, marginTop: 9 }}>{guideOk ? trip.guide : "TBD"}</div><span className={"rc-chip " + (guideOk ? "ok" : "tbd")} onClick={toggleGuide}>{guideOk ? <><CheckIcon /> Assigned</> : <><WarnIcon /> Assign on the session</>}</span></div>
        </div>

        {/* PRACTITIONERS */}
        <div className="sec-head">
          <div><h2>Practitioners</h2></div>
          <div className="rt">
            <span className="count">Everyone on this {scope === "boat" ? "departure" : "day"} — divers, snorkelers, free divers, and passengers</span>
            <button className="addbtn" onClick={addPassenger}><PlusIcon /> Add walk-up</button>
          </div>
        </div>
        <div className="ptable">
          <div className="phead"><span>Name</span><span>Nationality</span><span>Passport</span><span>Birthdate</span><span>Gender</span><span>Activity</span><span style={{ textAlign: "right" }}>Status</span></div>
          {trip.roster.map((pn) => {
            const blocking = missingFields(pn).length > 0;
            return (
              <div className={"prow " + (blocking ? "blocking" : "")} key={pn.id}>
                <span className="p-name">{pn.first} {pn.last} {pn.isMinor && <span className="minor-tag">Minor</span>}</span>
                <span>{cell(pn, "nationality", "Nationality")}</span>
                <span>{cell(pn, "passport", "Passport")}</span>
                <span>{cell(pn, "dob", "Birthdate")}</span>
                <span>{cell(pn, "gender", "Gender")}</span>
                <span>{activityCell(pn)}</span>
                {statusCell(pn)}
                {guardianRow(pn)}
              </div>
            );
          })}
        </div>

        {/* CREW & PROFESSIONAL */}
        <div className="sec-head"><div><h2>Crew &amp; Professional</h2></div><span className="count">The responsible guide is required on the notification; boat crew is entered in the authority portal</span></div>
        <div className="crew">
          <div className="crew-card">
            <div className="cc-label"><span>Responsible Professional</span></div>
            <div className="crew-person">
              <span className={"crew-av " + (guideOk ? "" : "tbd")}>{guideOk ? initials(trip.guide!.split(" ")[0], trip.guide!.split(" ")[1] || "") : "?"}</span>
              <div className="crew-info"><div className="crew-name">{guideOk ? trip.guide : "Not yet assigned"}</div><div className="crew-role">{guideOk ? "Dive Guide · assigned on the session · responsible for the trip" : "Assign a divemaster or instructor on the Schedule"}</div></div>
              <button className="assignbtn" onClick={toggleGuide}>{guideOk ? "Set TBD" : "Assign"}</button>
            </div>
          </div>
          <div className="crew-card deferred">
            <div className="cc-label"><span>Boat Crew &amp; First-aid</span><span className="cc-pf">Pathfinder</span></div>
            <div className="deferred-msg"><b>Not tracked in this release.</b> Boat crew — captain, deckhand — and the first-aid-certificate check are not modelled yet, so the page does not claim them. The authority captures crew in its portal, not in the practitioners file. Recorded, not invented.</div>
          </div>
        </div>

        {/* ACTION BAR */}
        <div className={"actions " + (go && J.built ? "go" : "")}>
          <div className="act-info">
            <div className="ai-t">{actionTitle}</div>
            <div className="ai-s">{actionSub}</div>
          </div>
          <button className="btn btn-ghost" onClick={printCopy}><PrintIcon /> Printable copy</button>
          <button className={"btn btn-primary " + (go && J.built ? "go" : "")} onClick={openGenerate}><DownloadIcon /> {J.genLabel}</button>
        </div>

        <div className="note"><InfoIcon /><span>{J.built
          ? <><b>How submission works.</b> AQUORIX generates the exact CDWS offline notification file — same sheet, same eleven headers, same controlled vocabularies — so it validates on upload. You upload it to trips.cdws.travel or submit it as the offline form, then pay the CDWS permit fee through their system. Keep a printed copy on board, as the authority requires. AQUORIX produces the file; the operator submits.</>
          : <><b>Aqaba is prepared, not blocked.</b> The identity spine, the readiness check, and the roster are identical across jurisdictions. The nationality translation is keyed by authority, so an ASEZA label set is data rather than rework. When the official ASEZA form is obtained, one output writer is added beside the Egyptian one, and this same surface produces it. AQUORIX produces the file; the operator submits.</>}</span></div>

        <div className="proto-tag">AQUORIX — Manifest · one surface, authority-aware output · Tropical Money palette</div>
      </div>

      {/* MODAL + TOAST — portaled to <body> so position:fixed escapes any transformed ancestor in the dashboard shell */}
      {createPortal(
        <div className="manifest-page manifest-overlay">
      <div className={"mask " + (modal ? "on" : "")} onClick={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
        <div className="manifest-modal">
          {modal === "cdws" && (() => {
            const rd = readiness;
            return (
              <>
                <div className="modal-h"><h3>CDWS file preview — GuestList.xlsx</h3><button className="mx" onClick={() => setModal(null)}>×</button></div>
                <div className="modal-b">
                  <div className="xlsx-wrap"><table className="xlsx"><thead><tr>{HEADERS.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                    <tbody>{trip.roster.map((pn) => {
                      const cells = [pn.first, pn.last, pn.dob, pn.nationality, pn.gender, pn.passport, pn.activity, pn.accommodation, null, pn.isMinor ? pn.guardianPassport : null, pn.isMinor ? pn.guardianNationality : null];
                      return (<tr key={pn.id}>{cells.map((c, i) => { const req = [0, 1, 2, 3, 4, 5, 6].includes(i); const blank = c == null || c === ""; return (<td key={i} className={blank && req ? "blank" : ""}>{blank ? (req ? "— missing —" : "") : c}</td>); })}</tr>);
                    })}</tbody></table></div>
                  <div className="modal-note"><b>This is exactly what gets written</b> — sheet <code>GuestList</code>, the eleven headers verbatim (including the <b>Accomodation</b> spelling on the form), the controlled Activity and Nationality vocabularies, and the ninety-nine-row limit. Nationality is written as the exact CDWS label. Columns H and I never block. Columns J and K are written for minors only.<br /><br />
                    {rd.blocking === 0
                      ? <><b style={{ color: "var(--reef-deep)" }}>Complete.</b> Every required cell is filled. In the build, this writes the .xlsx and offers download plus a printable copy.</>
                      : <><b style={{ color: "var(--att-deep)" }}>{rd.blocking} row{rd.blocking > 1 ? "s" : ""} incomplete.</b> The highlighted cells are required and still empty. You may still generate; the file will carry the gaps.</>}</div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
                    <button className="btn btn-ghost" onClick={() => setModal(null)}>Close</button>
                    <button className="btn btn-primary" onClick={downloadCdwsFile}><DownloadIcon /> Write the file</button>
                  </div>
                </div>
              </>
            );
          })()}
          {modal === "aseza" && (() => {
            const rd = readiness;
            const fields = ["FirstName", "LastName", "Certification level", "ID type", "ID number", "Nationality"];
            return (
              <>
                <div className="modal-h"><h3>Aqaba · ASEZA — prepared, pending the official form</h3><button className="mx" onClick={() => setModal(null)}>×</button></div>
                <div className="modal-b">
                  <div className="aseza-box">
                    <h4>The roster is ready. The ASEZA writer is not built yet — and that is by design.</h4>
                    <p>The ASEZA submission format is not yet in hand, so AQUORIX does not guess it. What is built is everything upstream of the file: the same practitioner roster, the same readiness check, and the same authority-keyed identity. {rd.blocking === 0 ? "This roster is complete and would map straight through." : "This roster has " + rd.blocking + " practitioner" + (rd.blocking > 1 ? "s" : "") + " still missing a required field — the same check as Egypt."}</p>
                    <p style={{ marginTop: 10 }}>The observed ASEZA fields, ready to map when the form is confirmed:</p>
                    <div className="aseza-fields">{fields.map((f) => <span key={f}>{f}</span>)}</div>
                  </div>
                  <div className="modal-note"><b>Why this never blocks.</b> An operator in Aqaba assembles and checks the roster exactly as in Egypt. When ASEZA provides the official form, one output writer is added beside the Egyptian one and this same surface produces it — no new page, no new workflow. Nationality already translates by authority, so the Aqaba label set is data, not rework.</div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
                    <button className="btn btn-ghost" onClick={() => setModal(null)}>Close</button>
                    <button className="btn btn-primary" onClick={() => { setModal(null); toast("Roster prepared. The ASEZA writer is added when the official form is obtained."); }}><CheckIcon /> Prepare roster</button>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* TOAST */}
      <div className={"toast " + (toastMsg ? "on" : "")}>{toastMsg}</div>
        </div>,
        document.body
      )}

      {/* PRINT VIEW */}
      <div className="printview">
        <div className="pv-h">Manifest — {trip.boat}</div>
        <div className="pv-sub">{J.center} · {trip.time} · {J.label} · Responsible professional: {trip.guide || "TBD"}</div>
        <table className="pv-t"><thead><tr><th>#</th><th>Name</th><th>Nationality</th><th>Passport</th><th>Birthdate</th><th>Gender</th><th>Activity</th></tr></thead>
          <tbody>{trip.roster.map((p, i) => (<tr key={p.id}><td>{i + 1}</td><td>{p.first} {p.last}{p.isMinor ? " (minor)" : ""}</td><td>{p.nationality || "—"}</td><td>{p.passport ? maskPass(p.passport) : "—"}</td><td>{p.dob || "—"}</td><td>{p.gender || "—"}</td><td>{p.activity}</td></tr>))}</tbody></table>
        <div className="pv-crew">Responsible professional: {trip.guide || "TBD"} &nbsp;·&nbsp; Boat crew &amp; first-aid: recorded in the authority portal.</div>
      </div>
    </div>
  );
}
